import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { RefreshCw, X } from "lucide-react";
import { registerSW } from "virtual:pwa-register";
import { Button } from "@/components/ui/button";
import { Card, CardDescription, CardFooter, CardHeader, CardTitle } from "@/components/ui/card";

const UPDATE_CHECK_MS = 60 * 60 * 1000;

export default function PWAUpdatePrompt() {
  const { t } = useTranslation();
  const [visible, setVisible] = useState(false);
  const updateSWRef = useRef<((reloadPage?: boolean) => Promise<void>) | null>(null);

  useEffect(() => {
    let aborted = false;
    const cleanups: Array<() => void> = [];

    const show = () => {
      if (!aborted) setVisible(true);
    };

    const updateSW = registerSW({
      immediate: true,
      onNeedRefresh() {
        show();
      },
      onRegistered(registration) {
        if (aborted || !registration) return;

        // Belt-and-suspenders: workbox-window's onNeedRefresh can miss an update on
        // iOS standalone PWAs (SW already waiting on relaunch, or installs while the
        // page is backgrounded). Detect the waiting/installed worker ourselves too.
        const maybeShowFromWaiting = () => {
          if (registration.waiting && navigator.serviceWorker.controller) show();
        };

        const onUpdateFound = () => {
          const installing = registration.installing;
          if (!installing) return;
          installing.addEventListener("statechange", () => {
            if (installing.state === "installed" && navigator.serviceWorker.controller) {
              show();
            }
          });
        };

        const tick = () => {
          void registration.update();
          maybeShowFromWaiting();
        };

        registration.addEventListener("updatefound", onUpdateFound);
        maybeShowFromWaiting();

        const intervalId = window.setInterval(tick, UPDATE_CHECK_MS);
        const onPageShow = () => tick();
        const onVisibility = () => {
          if (document.visibilityState === "visible") tick();
        };

        window.addEventListener("pageshow", onPageShow);
        document.addEventListener("visibilitychange", onVisibility);
        tick();

        cleanups.push(() => clearInterval(intervalId));
        cleanups.push(() => window.removeEventListener("pageshow", onPageShow));
        cleanups.push(() => document.removeEventListener("visibilitychange", onVisibility));
        cleanups.push(() => registration.removeEventListener("updatefound", onUpdateFound));
      },
    });

    updateSWRef.current = updateSW;

    return () => {
      aborted = true;
      for (const fn of cleanups) fn();
      updateSWRef.current = null;
    };
  }, []);

  const handleUpdate = () => {
    void updateSWRef.current?.(true);
  };

  const handleLater = () => {
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <Card className="fixed bottom-28 left-4 right-4 z-[60] shadow-lg md:bottom-4 md:left-auto md:right-4 md:w-96">
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-2">
          <div className="min-w-0 flex-1">
            <CardTitle className="text-lg">{t("pwa.updateAvailableTitle")}</CardTitle>
            <CardDescription className="mt-1">{t("pwa.updateAvailableDescription")}</CardDescription>
          </div>
          <button
            type="button"
            onClick={handleLater}
            className="shrink-0 text-muted-foreground hover:text-foreground"
            aria-label={t("pwa.close")}
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </CardHeader>
      <CardFooter className="flex flex-col gap-2 pt-0 sm:flex-row sm:justify-end">
        <Button type="button" variant="outline" className="w-full sm:w-auto" onClick={handleLater}>
          {t("pwa.updateLater")}
        </Button>
        <Button type="button" className="w-full sm:w-auto" onClick={handleUpdate}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t("pwa.updateNow")}
        </Button>
      </CardFooter>
    </Card>
  );
}
